// Consulta de Vales
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { 
  Search, Filter, Download, CheckCircle, Clock, XCircle,
  AlertTriangle, CreditCard, FileText, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const ConsultaVales = ({ user }) => {
  const [vales, setVales] = useState([]);
  const [parcerias, setParcerias] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroParceria, setFiltroParceria] = useState('');
  const [filtroMetodo, setFiltroMetodo] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [pesquisa, setPesquisa] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const isJaneira = ['admin', 'responsavel', 'colaborador'].includes(user?.role);
  const canManage = ['admin', 'responsavel'].includes(user?.role);

  useEffect(() => {
    loadData();
  }, [filtroEstado, filtroParceria, filtroMetodo, filtroDataInicio, filtroDataFim]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Carregar parcerias
      if (isJaneira) {
        const parceriasRes = await api.get('/parcerias/entidades');
        setParcerias(parceriasRes.data);
      }

      // Construir query params
      const params = new URLSearchParams();
      if (filtroEstado) params.append('estado', filtroEstado);
      if (filtroParceria) params.append('parceria_id', filtroParceria);
      if (filtroMetodo) params.append('metodo', filtroMetodo);
      if (filtroDataInicio) params.append('data_inicio', filtroDataInicio);
      if (filtroDataFim) params.append('data_fim', filtroDataFim);

      const valesRes = await api.get(`/parcerias/vales?${params.toString()}`);
      setVales(valesRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar vales');
    } finally {
      setLoading(false);
    }
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'Válido':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Válido</Badge>;
      case 'Utilizado':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Utilizado</Badge>;
      case 'Expirado':
        return <Badge variant="destructive" className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Expirado</Badge>;
      default:
        return <Badge>{estado}</Badge>;
    }
  };

  const getMetodoBadge = (metodo) => {
    if (metodo === 'Contingência') {
      return <Badge variant="warning" className="bg-orange-100 text-orange-700"><AlertTriangle className="h-3 w-3 mr-1" />Manual</Badge>;
    }
    return <Badge variant="outline"><CreditCard className="h-3 w-3 mr-1" />Cartão</Badge>;
  };

  const handleDeleteVale = async () => {
    if (!showDeleteConfirm) return;
    try {
      await api.delete(`/parcerias/vales/${showDeleteConfirm.id}`);
      toast.success('Vale eliminado com sucesso');
      setShowDeleteConfirm(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar vale');
    }
  };

  const limparFiltros = () => {
    setFiltroEstado('');
    setFiltroParceria('');
    setFiltroMetodo('');
    setFiltroDataInicio('');
    setFiltroDataFim('');
    setPesquisa('');
  };

  const exportarCSV = () => {
    const headers = ['Número', 'Parceria', 'Cliente', 'Emitido Por', 'Valor Compra', 'Desconto', 'Estado', 'Método', 'Data Emissão', 'Validade', 'Validado Por'];
    const rows = valesFiltrados.map(v => [
      v.numero_vale,
      v.nome_parceria || '-',
      v.nome_cliente || '-',
      v.nome_registado_por || '-',
      `€${v.valor_compra?.toFixed(2)}`,
      `€${v.valor_desconto_aplicado?.toFixed(2)}`,
      v.estado,
      v.metodo,
      new Date(v.data_emissao).toLocaleDateString('pt-PT'),
      new Date(v.data_validade).toLocaleDateString('pt-PT'),
      v.nome_validado_por || '-'
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vales_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Filtrar por pesquisa
  const valesFiltrados = vales.filter(vale => {
    if (!pesquisa) return true;
    const termo = pesquisa.toLowerCase();
    return (
      vale.numero_vale?.toLowerCase().includes(termo) ||
      vale.nome_parceria?.toLowerCase().includes(termo) ||
      vale.nome_cliente?.toLowerCase().includes(termo) ||
      vale.numero_fatura?.toLowerCase().includes(termo) ||
      vale.id_cartao_rfid?.toLowerCase().includes(termo)
    );
  });

  return (
    <div className="space-y-4" data-testid="consulta-vales">
      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Pesquisa */}
            <div className="lg:col-span-2">
              <Label>Pesquisa</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="N.º vale, cliente, fatura, cartão..."
                  value={pesquisa}
                  onChange={(e) => setPesquisa(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Estado */}
            <div>
              <Label>Estado</Label>
              <Select value={filtroEstado || "all"} onValueChange={(v) => setFiltroEstado(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="Válido">Válido</SelectItem>
                  <SelectItem value="Utilizado">Utilizado</SelectItem>
                  <SelectItem value="Expirado">Expirado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Parceria */}
            {isJaneira && (
              <div>
                <Label>Parceria</Label>
                <Select value={filtroParceria || "all"} onValueChange={(v) => setFiltroParceria(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {parcerias.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.nome_entidade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Método */}
            <div>
              <Label>Método</Label>
              <Select value={filtroMetodo || "all"} onValueChange={(v) => setFiltroMetodo(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="RFID">Cartão</SelectItem>
                  <SelectItem value="Contingência">Contingência</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Datas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            <div>
              <Label>Data Início</Label>
              <Input
                type="date"
                value={filtroDataInicio}
                onChange={(e) => setFiltroDataInicio(e.target.value)}
              />
            </div>
            <div>
              <Label>Data Fim</Label>
              <Input
                type="date"
                value={filtroDataFim}
                onChange={(e) => setFiltroDataFim(e.target.value)}
              />
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" onClick={limparFiltros}>
                Limpar
              </Button>
              <Button variant="outline" onClick={exportarCSV}>
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Vales */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              Vales ({valesFiltrados.length})
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : valesFiltrados.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum vale encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Parceria</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Emitido Por</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="text-right">Desconto</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Método</TableHead>
                    <TableHead>Emissão</TableHead>
                    <TableHead>Validade</TableHead>
                    <TableHead>Validado Por</TableHead>
                    {canManage && <TableHead className="text-right">Ações</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {valesFiltrados.map(vale => (
                    <TableRow key={vale.id}>
                      <TableCell className="font-mono text-sm">{vale.numero_vale}</TableCell>
                      <TableCell>{vale.nome_parceria || '-'}</TableCell>
                      <TableCell>{vale.nome_cliente || '-'}</TableCell>
                      <TableCell>{vale.nome_registado_por || '-'}</TableCell>
                      <TableCell className="text-right">€{vale.valor_compra?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium">€{vale.valor_desconto_aplicado?.toFixed(2)}</TableCell>
                      <TableCell>{getEstadoBadge(vale.estado)}</TableCell>
                      <TableCell>{getMetodoBadge(vale.metodo)}</TableCell>
                      <TableCell>{new Date(vale.data_emissao).toLocaleDateString('pt-PT')}</TableCell>
                      <TableCell>
                        <span className={vale.estado === 'Expirado' ? 'text-red-600' : ''}>
                          {new Date(vale.data_validade).toLocaleDateString('pt-PT')}
                        </span>
                      </TableCell>
                      <TableCell>{vale.nome_validado_por || '—'}</TableCell>
                      {canManage && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => setShowDeleteConfirm(vale)}
                            data-testid={`btn-delete-vale-${vale.numero_vale}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Confirmar Eliminação de Vale */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Vale</DialogTitle>
            <DialogDescription>
              Tem a certeza que deseja eliminar o vale "{showDeleteConfirm?.numero_vale}"?
              Esta ação é irreversível.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 text-sm text-muted-foreground">
            <p>Parceria: <strong>{showDeleteConfirm?.nome_parceria || '-'}</strong></p>
            <p>Desconto: <strong>€{showDeleteConfirm?.valor_desconto_aplicado?.toFixed(2)}</strong></p>
            <p>Estado: <strong>{showDeleteConfirm?.estado}</strong></p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeleteVale} data-testid="btn-confirmar-delete-vale">
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsultaVales;
